import { ApiErrorStatusCode } from 'lib-shared-auth';
import { Sentry, SentryTeam } from 'lib-shared-sentry';
import { useCallback, useRef } from 'react';

import { aiAgentsApi } from 'apis/aiAgents';

import { type MimUIMessage } from '../utils/sdk/structuredResponse';
import { generateChatTitle } from '../utils/sdk/title';
import { toSidekickMessageDraft } from '../utils/sidekickMessages';

const SDK_VERSION = '7';

/** Named per turn, so switching chats mid-stream cannot redirect it. */
type PersistTurnArg = {
  chatId: string;
  messages: MimUIMessage[];
};

type UseSidekickPersistenceResult = {
  persistTurn: (arg: PersistTurnArg) => Promise<void>;
  markExisting: (chatId: string) => void;
};

const isConflict = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'status' in error &&
  error.status === ApiErrorStatusCode.Conflict;

export const useSidekickPersistence = (): UseSidekickPersistenceResult => {
  const [createChat] =
    aiAgentsApi.useCreateAiAgentsSidekickConversationMutation();
  const [appendMessages] =
    aiAgentsApi.useCreateAiAgentsSidekickConversationMessagesMutation();

  const createdRef = useRef(new Map<string, Promise<void>>());

  const markExisting = useCallback((id: string) => {
    createdRef.current.set(id, Promise.resolve());
  }, []);

  const ensureChat = useCallback(
    (
      chatId: string,
      getTitle: () => Promise<string | undefined>,
    ): Promise<void> => {
      const existing = createdRef.current.get(chatId);
      if (existing) return existing;

      const done = getTitle()
        .then((title) =>
          createChat({
            id: chatId,
            title,
            sdkVersion: SDK_VERSION,
          }).unwrap(),
        )
        .then(() => undefined)
        .catch((error: unknown) => {
          if (isConflict(error)) return;
          createdRef.current.delete(chatId);
          throw error;
        });

      createdRef.current.set(chatId, done);
      return done;
    },
    [createChat],
  );

  const appendsRef = useRef(new Map<string, Promise<void>>());

  const persistTurn = useCallback(
    async ({ chatId, messages }: PersistTurnArg) => {
      const stored = messages.map(toSidekickMessageDraft);
      if (stored.length === 0) return;

      const save = async () => {
        try {
          await ensureChat(chatId, () => generateChatTitle(messages));
          await appendMessages({
            conversationId: chatId,
            sdkVersion: SDK_VERSION,
            messages: stored,
          }).unwrap();
        } catch (exception) {
          Sentry.captureException(
            new Error('Sidekick: chat turn was not saved'),
            {
              tags: { team: SentryTeam.Automation },
              extra: {
                chatId,
                messageIds: stored
                  .map((message) => message.messageId)
                  .join(', '),
                exception,
              },
            },
          );
        }
      };

      // The backend numbers messages without a lock, so appends to one chat must not overlap.
      const done = (appendsRef.current.get(chatId) ?? Promise.resolve()).then(
        save,
      );
      appendsRef.current.set(chatId, done);
      await done;
    },
    [appendMessages, ensureChat],
  );

  return { persistTurn, markExisting };
};
